import { Spinner } from "components/DesignSystem/Loader/loader";
import { useQuery } from "hooks/useQuery";
import TaskSuccessfulTick from "static/images/TaskSuccessfulTick.svg";
import { useState } from "react";
import { FinancialSummaryPrint } from "./FinancialSummaryPrint";
import {
  ALL_SECTION_KEYS,
  DEFAULT_FORECAST_GROUP_ORDER,
  DEFAULT_FORECAST_VISIBLE_SECTIONS,
  DEFAULT_GROUP_ORDER,
  DEFAULT_VISIBLE_SECTIONS,
  ExportAnalysisTypes,
  SectionKey,
  getAllowedGroupOrder,
  getAllowedVisibleSections,
} from "constants/financialSummary";

export type FinancialSummaryExportParams = {
  filter: { START_DATE: string; END_DATE: string; PERIOD: string };
  visibleSections: Partial<Record<SectionKey, boolean>>;
  groupOrder: string[];
  periodText: string;
  groupId: string;
  analysisTypes: ExportAnalysisTypes;
  isForecastEnabled: boolean;
  forecastStartDate?: string;
  forecastEndDate?: string;
  assumptionSetId?: string;
};

export const FinancialSummaryExportPage = () => {
  const query = useQuery();
  const [exportState, setExportState] = useState<
    "loading" | "success" | "error"
  >("loading");

  const entityId = query.get("entity") ?? "";
  const startDate = query.get("START_DATE");
  const endDate = query.get("END_DATE");
  const period = query.get("PERIOD");

  const isForecastEnabled = query.get("FORECAST_ENABLED") === "true";
  const forecastStartDate = query.get("FORECAST_START") ?? undefined;
  const forecastEndDate = query.get("FORECAST_END") ?? undefined;
  const assumptionSetId = query.get("FORECAST_SET_ID") ?? undefined;

  const defaultVisibleSections = isForecastEnabled
    ? DEFAULT_FORECAST_VISIBLE_SECTIONS
    : DEFAULT_VISIBLE_SECTIONS;
  const rawVisibleSections = { ...defaultVisibleSections };
  for (const key of ALL_SECTION_KEYS) {
    const val = query.get(key);
    if (val !== null) rawVisibleSections[key] = val === "true";
  }
  const visibleSections = getAllowedVisibleSections(
    rawVisibleSections,
    isForecastEnabled
  );

  const analysisTypes: ExportAnalysisTypes = {
    revenueAnalysisType:
      query.get("revenueAnalysisType") === "CLASSIFICATION"
        ? "CLASSIFICATION"
        : "CATEGORY",
    spendAnalysisType:
      query.get("spendAnalysisType") === "CLASSIFICATION"
        ? "CLASSIFICATION"
        : "CATEGORY",
    arViewType:
      query.get("arViewType") === "invoices" ? "invoices" : "customers",
    apViewType: query.get("apViewType") === "bills" ? "bills" : "vendors",
  };

  const defaultGroupOrder = isForecastEnabled
    ? DEFAULT_FORECAST_GROUP_ORDER
    : DEFAULT_GROUP_ORDER;
  const rawGroupOrder =
    query.get("groupOrder")?.split(",") ?? defaultGroupOrder;
  const groupOrder = getAllowedGroupOrder(rawGroupOrder, isForecastEnabled);
  const periodText = query.get("periodText") ?? "";
  const groupId = query.get("groupId") ?? "";

  const params: FinancialSummaryExportParams | null =
    startDate && endDate && period
      ? {
          filter: { START_DATE: startDate, END_DATE: endDate, PERIOD: period },
          visibleSections,
          groupOrder,
          periodText,
          groupId,
          analysisTypes,
          isForecastEnabled,
          forecastStartDate,
          forecastEndDate,
          assumptionSetId,
        }
      : null;

  const handleComplete = (success: boolean) => {
    setExportState(success ? "success" : "error");
    if (success) {
      setTimeout(() => window.close(), 2000);
    }
  };

  const TITLES = {
    loading: "Exporting Financial Summary",
    success: "Export complete",
    error: "Export failed",
  };

  const SUBTITLES = {
    loading: "This might take some time",
    success: "This tab will close automatically",
    error: "Something went wrong. Please try again.",
  };

  return (
    <div className="t-flex t-flex-col t-items-center t-justify-center t-h-screen t-gap-4">
      {exportState === "loading" && <Spinner />}
      {exportState === "success" && (
        <img
          src={TaskSuccessfulTick}
          alt="Export complete"
          className="t-w-12 t-h-12"
        />
      )}
      <div className="t-flex t-flex-col t-items-center t-gap-1">
        <span className="t-text-subtitle t-font-semibold t-text-text-100">
          {TITLES[exportState]}
        </span>
        <span className="t-text-body-sm t-text-text-30">
          {SUBTITLES[exportState]}
        </span>
      </div>

      {params && entityId && (
        <div className="t-fixed t-top-0 t-left-full t-w-[1200px] t-overflow-hidden t-pointer-events-none t-z-[-1] t-opacity-0">
          <FinancialSummaryPrint
            onComplete={handleComplete}
            filter={params.filter}
            exportSections={params.visibleSections}
            groupOrder={params.groupOrder}
            periodText={params.periodText}
            groupId={params.groupId ?? ""}
            isCustomDateRange={params.filter.PERIOD === "custom"}
            analysisTypes={params.analysisTypes}
            isForecastEnabled={params.isForecastEnabled}
            forecastStartDate={params.forecastStartDate}
            forecastEndDate={params.forecastEndDate}
            assumptionSetId={params.assumptionSetId}
          />
        </div>
      )}
    </div>
  );
};
